import api from './api';
import {
  Tenant,
  TenantDetails,
  TenantEmployee,
  TenantOperations,
  TenantFinancials,
  TenantApiUsage,
  AuditLog,
  DashboardStats,
  RevenueChartPoint,
  SubscriptionPlan,
  Announcement,
  CreditTransaction,
  SystemApiStats,
} from '../types/tenant';

export interface TenantInviteCode {
  id: number;
  code: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
  expiresAt: string | null;
  usedAt: string | null;
  createdAt: string;
  note: string | null;
  intendedCompanyName: string | null;
  intendedContactEmail: string | null;
  intendedPlanId: number | null;
  intendedPlanName: string | null;
  createdByName: string | null;
  usedByUserName: string | null;
  usedByCompanyId: number | null;
  usedByCompanyName: string | null;
}

function transformTenantInviteCode(r: Record<string, unknown>): TenantInviteCode {
  return {
    id: r.id as number,
    code: r.code as string,
    status: r.status as TenantInviteCode['status'],
    expiresAt: (r.expires_at || r.expiresAt) as string | null,
    usedAt: (r.used_at || r.usedAt) as string | null,
    createdAt: (r.created_at || r.createdAt) as string,
    note: (r.note as string) || null,
    intendedCompanyName: (r.intended_company_name || r.intendedCompanyName || null) as string | null,
    intendedContactEmail: (r.intended_contact_email || r.intendedContactEmail || null) as string | null,
    intendedPlanId: (r.intended_plan_id ?? r.intendedPlanId ?? null) as number | null,
    intendedPlanName: (r.intended_plan_name || r.intendedPlanName || null) as string | null,
    createdByName: (r.created_by_name || r.createdByName || null) as string | null,
    usedByUserName: (r.used_by_user_name || r.usedByUserName || null) as string | null,
    usedByCompanyId: (r.used_by_company_id ?? r.usedByCompanyId ?? null) as number | null,
    usedByCompanyName: (r.used_by_company_name || r.usedByCompanyName || null) as string | null,
  };
}

// ============ Transform helpers ============

function transformTenant(row: Record<string, unknown>): Tenant {
  return {
    id: row.id as number,
    companyName: (row.company_name || row.companyName) as string,
    contactName: (row.contact_name || row.contactName) as string,
    contactEmail: (row.contact_email || row.contactEmail) as string,
    contactPhone: (row.contact_phone || row.contactPhone) as string | undefined,
    address: row.address as string | undefined,
    companyStatus: (row.company_status || row.companyStatus) as string,
    planName: (row.plan_name || row.planName) as string,
    planSlug: (row.plan_slug || row.planSlug) as Tenant['planSlug'],
    basePrice: parseFloat(row.base_price as string) || 0,
    subscriptionStatus: (row.subscription_status || row.subscriptionStatus) as Tenant['subscriptionStatus'],
    trialEndsAt: (row.trial_ends_at || row.trialEndsAt) as string | undefined,
    nextBillingDate: (row.next_billing_date || row.nextBillingDate) as string | undefined,
    credits: parseFloat(row.credits as string) || 0,
    employeeCount: parseInt(row.employee_count as string) || 0,
    driverCount: parseInt(row.driver_count as string) || 0,
    dispatcherCount: parseInt(row.dispatcher_count as string) || 0,
    truckCount: parseInt(row.truck_count as string) || 0,
    createdAt: (row.created_at || row.createdAt) as string,
  };
}

function transformTenantDetails(row: Record<string, unknown>): TenantDetails {
  return {
    id: row.id as number,
    name: row.name as string,
    contactName: (row.contact_name || row.contactName) as string,
    contactEmail: (row.contact_email || row.contactEmail) as string,
    contactPhone: (row.contact_phone || row.contactPhone) as string | undefined,
    address: row.address as string | undefined,
    city: row.city as string | undefined,
    state: row.state as string | undefined,
    zipCode: (row.zip_code || row.zipCode) as string | undefined,
    taxId: (row.tax_id || row.taxId) as string | undefined,
    industry: row.industry as string | undefined,
    website: row.website as string | undefined,
    mcDot: (row.mc_dot || row.mcDot) as string | undefined,
    serviceArea: (row.service_area || row.serviceArea) as string | undefined,
    createdAt: (row.created_at || row.createdAt) as string,
    boss: row.boss ? {
      id: (row.boss as Record<string, unknown>).id as number,
      username: (row.boss as Record<string, unknown>).username as string,
      email: (row.boss as Record<string, unknown>).email as string,
      createdAt: ((row.boss as Record<string, unknown>).created_at || (row.boss as Record<string, unknown>).createdAt) as string,
      isActive: ((row.boss as Record<string, unknown>).is_active ?? true) as boolean,
    } : null,
    subscriptionId: (row.subscription_id || row.subscriptionId) as number,
    subscriptionStatus: (row.subscription_status || row.subscriptionStatus) as TenantDetails['subscriptionStatus'],
    planId: (row.plan_id || row.planId) as number,
    planName: (row.plan_name || row.planName) as string,
    planSlug: (row.plan_slug || row.planSlug) as TenantDetails['planSlug'],
    basePrice: parseFloat(row.base_price as string) || 0,
    billingCycle: (row.billing_cycle || row.billingCycle || 'monthly') as TenantDetails['billingCycle'],
    currentPeriodStart: (row.current_period_start || row.currentPeriodStart) as string | undefined,
    currentPeriodEnd: (row.current_period_end || row.currentPeriodEnd) as string | undefined,
    trialEndsAt: (row.trial_ends_at || row.trialEndsAt) as string | undefined,
    credits: parseFloat(row.credits as string) || 0,
    discountType: (row.discount_type || row.discountType) as TenantDetails['discountType'],
    discountValue: row.discount_value != null ? parseFloat(row.discount_value as string) : undefined,
    discountExpiresAt: (row.discount_expires_at || row.discountExpiresAt) as string | undefined,
    cancelledAt: (row.cancelled_at || row.cancelledAt) as string | undefined,
    suspendedAt: (row.suspended_at || row.suspendedAt) as string | undefined,
    suspendReason: (row.suspend_reason || row.suspendReason) as string | undefined,
    maxTrucks: (row.max_trucks || row.maxTrucks) as number | undefined,
    maxDrivers: (row.max_drivers || row.maxDrivers) as number | undefined,
    maxDispatchers: (row.max_dispatchers || row.maxDispatchers) as number | undefined,
    maxApiCallsPerMonth: (row.max_api_calls_per_month || row.maxApiCallsPerMonth) as number | undefined,
    planFeatures: ((row.plan_features || row.planFeatures) as Record<string, boolean>) || {},
    employeeCounts: ((row.employee_counts || row.employeeCounts) as Record<string, number>) || {},
  };
}

function transformApiUsage(raw: Record<string, unknown>): TenantApiUsage {
  const data = raw as { daily: Record<string, unknown>[]; topEndpoints: Record<string, unknown>[] };
  return {
    daily: (data.daily || []).map(d => ({
      date: d.date as string,
      totalCalls: parseInt(d.total_calls as string) || 0,
      avgResponseTime: parseInt(d.avg_response_time as string) || 0,
      errorCount: parseInt(d.error_count as string) || 0,
    })),
    topEndpoints: (data.topEndpoints || data.topEndpoints || []).map(e => ({
      method: e.method as string,
      path: e.path as string,
      callCount: parseInt(e.call_count as string) || 0,
      avgResponseTime: parseInt(e.avg_response_time as string) || 0,
    })),
  };
}

function transformPlan(row: Record<string, unknown>): SubscriptionPlan {
  return {
    id: row.id as number,
    name: row.name as string,
    slug: row.slug as string,
    basePrice: parseFloat(row.base_price as string) || 0,
    billingCycle: (row.billing_cycle || 'monthly') as string,
    maxTrucks: row.max_trucks as number | undefined,
    maxDrivers: row.max_drivers as number | undefined,
    maxDispatchers: row.max_dispatchers as number | undefined,
    maxApiCallsPerMonth: row.max_api_calls_per_month as number | undefined,
    maxStorageGb: row.max_storage_gb as number | undefined,
    features: (row.features as Record<string, boolean>) || {},
    isActive: (row.is_active ?? true) as boolean,
    sortOrder: (row.sort_order || 0) as number,
    subscriberCount: parseInt(row.subscriber_count as string) || 0,
    createdAt: (row.created_at || row.createdAt) as string,
    updatedAt: (row.updated_at || row.updatedAt) as string,
  };
}

// ============ API Service ============

export const adminApi = {
  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get('/admin/dashboard/stats');
    const d = response.data;
    return {
      totalTenants: parseInt(d.total_tenants) || 0,
      activeTenants: parseInt(d.active_tenants) || 0,
      trialTenants: parseInt(d.trial_tenants) || 0,
      suspendedTenants: parseInt(d.suspended_tenants) || 0,
      mrr: parseFloat(d.mrr) || 0,
      expiringTrials: parseInt(d.expiring_trials) || 0,
    };
  },

  async getRevenueChart(months = 12): Promise<RevenueChartPoint[]> {
    const response = await api.get('/admin/dashboard/revenue-chart', { params: { months } });
    return (response.data as Record<string, unknown>[]).map(r => ({
      month: r.month as string,
      mrr: parseFloat(r.mrr as string) || 0,
    }));
  },

  // Tenants
  async getTenants(params: {
    search?: string;
    plan?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ data: Tenant[]; total: number }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });
    const response = await api.get(`/admin/tenants?${searchParams.toString()}`);
    // Paginated response: interceptor strips success, keeps {data, total, limit, offset}
    return {
      data: (response.data.data as Record<string, unknown>[]).map(transformTenant),
      total: response.data.total as number,
    };
  },

  async getTenantDetails(id: number): Promise<TenantDetails> {
    const response = await api.get(`/admin/tenants/${id}`);
    return transformTenantDetails(response.data as Record<string, unknown>);
  },

  async updateTenant(id: number, data: Record<string, unknown>): Promise<void> {
    await api.put(`/admin/tenants/${id}`, data);
  },

  async deleteTenant(id: number): Promise<void> {
    await api.delete(`/admin/tenants/${id}`);
  },

  // Subscription Operations
  async suspendTenant(id: number, reason: string): Promise<void> {
    await api.post(`/admin/tenants/${id}/suspend`, { reason });
  },

  async resumeTenant(id: number): Promise<void> {
    await api.post(`/admin/tenants/${id}/resume`);
  },

  async cancelTenant(id: number, reason: string): Promise<void> {
    await api.post(`/admin/tenants/${id}/cancel`, { reason });
  },

  async changePlan(id: number, planSlug: string): Promise<void> {
    await api.post(`/admin/tenants/${id}/change-plan`, { planSlug });
  },

  async manageCredits(id: number, amount: number, type: 'add' | 'deduct', reason: string): Promise<CreditTransaction> {
    const response = await api.post(`/admin/tenants/${id}/credits`, { amount, type, reason });
    return response.data as CreditTransaction;
  },

  async impersonateTenant(id: number): Promise<{ token: string; bossEmail: string }> {
    const response = await api.post(`/admin/tenants/${id}/impersonate`);
    return response.data as { token: string; bossEmail: string };
  },

  // Tenant Sub-data
  async getTenantEmployees(id: number, params?: { limit?: number; offset?: number }): Promise<{ data: TenantEmployee[]; total: number }> {
    const response = await api.get(`/admin/tenants/${id}/employees`, { params });
    return {
      data: (response.data.data as Record<string, unknown>[]).map(e => ({
        id: e.id as number,
        username: e.username as string,
        email: e.email as string,
        role: e.role as string,
        isActive: (e.is_active ?? true) as boolean,
        jobTitle: (e.job_title || e.jobTitle) as string | undefined,
        createdAt: (e.created_at || e.createdAt) as string,
      })),
      total: response.data.total as number,
    };
  },

  async getTenantOperations(id: number): Promise<TenantOperations> {
    const response = await api.get(`/admin/tenants/${id}/operations`);
    const d = response.data as Record<string, unknown>;
    return {
      ordersThisMonth: parseInt(d.orders_this_month as string) || 0,
      tasksThisMonth: parseInt(d.tasks_this_month as string) || 0,
      tasksCompletedThisMonth: parseInt(d.tasks_completed_this_month as string) || 0,
      totalTrucks: parseInt(d.total_trucks as string) || 0,
      activeDrivers: parseInt(d.active_drivers as string) || 0,
    };
  },

  async getTenantFinancials(id: number): Promise<TenantFinancials> {
    const response = await api.get(`/admin/tenants/${id}/financials`);
    const d = response.data as Record<string, unknown>;
    return {
      invoicesThisMonth: parseFloat(d.invoices_this_month as string) || 0,
      invoiceCountThisMonth: parseInt(d.invoice_count_this_month as string) || 0,
      paidThisMonth: parseFloat(d.paid_this_month as string) || 0,
    };
  },

  async getTenantApiUsage(id: number, days = 30): Promise<TenantApiUsage> {
    const response = await api.get(`/admin/tenants/${id}/api-usage`, { params: { days } });
    return transformApiUsage(response.data as Record<string, unknown>);
  },

  async getTenantAuditLogs(id: number, params?: { limit?: number; offset?: number }): Promise<AuditLog[]> {
    const response = await api.get(`/admin/tenants/${id}/audit-logs`, { params });
    return (response.data as Record<string, unknown>[]).map(a => ({
      id: a.id as number,
      action: a.action as string,
      details: (a.details || {}) as Record<string, unknown>,
      createdAt: (a.created_at || a.createdAt) as string,
      adminUsername: (a.admin_username || a.adminUsername) as string,
      adminEmail: (a.admin_email || a.adminEmail) as string,
    }));
  },

  // Plans
  async getPlans(): Promise<SubscriptionPlan[]> {
    const response = await api.get('/admin/plans');
    return (response.data as Record<string, unknown>[]).map(transformPlan);
  },

  async createPlan(data: Record<string, unknown>): Promise<SubscriptionPlan> {
    const response = await api.post('/admin/plans', data);
    return transformPlan(response.data as Record<string, unknown>);
  },

  async updatePlan(id: number, data: Record<string, unknown>): Promise<SubscriptionPlan> {
    const response = await api.put(`/admin/plans/${id}`, data);
    return transformPlan(response.data as Record<string, unknown>);
  },

  // Announcements
  async getAnnouncements(params?: { limit?: number; offset?: number }): Promise<{ data: Announcement[]; total: number }> {
    const response = await api.get('/admin/announcements', { params });
    return {
      data: (response.data.data as Record<string, unknown>[]).map(a => ({
        id: a.id as number,
        title: a.title as string,
        content: a.content as string,
        type: a.type as Announcement['type'],
        targetPlans: (a.target_plans || []) as string[],
        isActive: (a.is_active ?? true) as boolean,
        startsAt: (a.starts_at || a.startsAt) as string | undefined,
        expiresAt: (a.expires_at || a.expiresAt) as string | undefined,
        createdByName: (a.created_by_name || a.createdByName) as string | undefined,
        createdAt: (a.created_at || a.createdAt) as string,
        updatedAt: (a.updated_at || a.updatedAt) as string,
      })),
      total: response.data.total as number,
    };
  },

  async createAnnouncement(data: Record<string, unknown>): Promise<Announcement> {
    const response = await api.post('/admin/announcements', data);
    return response.data as Announcement;
  },

  async updateAnnouncement(id: number, data: Record<string, unknown>): Promise<Announcement> {
    const response = await api.put(`/admin/announcements/${id}`, data);
    return response.data as Announcement;
  },

  async deleteAnnouncement(id: number): Promise<void> {
    await api.delete(`/admin/announcements/${id}`);
  },

  // System
  async getSystemApiStats(days = 7): Promise<SystemApiStats> {
    const response = await api.get('/admin/system/api-stats', { params: { days } });
    const raw = response.data as Record<string, unknown>;
    return {
      daily: ((raw.daily || []) as Record<string, unknown>[]).map(d => ({
        date: d.date as string,
        totalCalls: parseInt(d.total_calls as string) || 0,
        avgResponseTime: parseInt(d.avg_response_time as string) || 0,
        p95ResponseTime: parseInt(d.p95_response_time as string) || 0,
        errorCount: parseInt(d.error_count as string) || 0,
        errorRate: parseFloat(d.error_rate as string) || 0,
      })),
      slowEndpoints: ((raw.slowEndpoints || raw.slow_endpoints || []) as Record<string, unknown>[]).map(e => ({
        method: e.method as string,
        path: e.path as string,
        callCount: parseInt(e.call_count as string) || 0,
        avgResponseTime: parseInt(e.avg_response_time as string) || 0,
        maxResponseTime: parseInt(e.max_response_time as string) || 0,
      })),
      topTenants: ((raw.topTenants || raw.top_tenants || []) as Record<string, unknown>[]).map(t => ({
        companyId: t.company_id as number,
        companyName: (t.company_name || t.companyName) as string,
        totalCalls: parseInt(t.total_calls as string) || 0,
        avgResponseTime: parseInt(t.avg_response_time as string) || 0,
      })),
    };
  },

  // Tenant Invite Codes (boss registration)
  async listTenantInviteCodes(params: { status?: string; limit?: number; offset?: number } = {}): Promise<{
    data: TenantInviteCode[];
    total: number;
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') searchParams.set(k, String(v));
    });
    const response = await api.get(`/admin/tenant-invite-codes?${searchParams.toString()}`);
    return {
      data: (response.data.data as Record<string, unknown>[]).map(transformTenantInviteCode),
      total: response.data.total as number,
    };
  },

  async createTenantInviteCode(data: {
    expiresInHours?: number;
    note?: string;
    intendedCompanyName?: string;
    intendedContactEmail?: string;
    intendedPlanId?: number;
  }): Promise<TenantInviteCode> {
    const response = await api.post('/admin/tenant-invite-codes', data);
    return transformTenantInviteCode(response.data as Record<string, unknown>);
  },

  async revokeTenantInviteCode(id: number): Promise<void> {
    await api.post(`/admin/tenant-invite-codes/${id}/revoke`);
  },

  // Audit Logs
  async getGlobalAuditLogs(params?: {
    action?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: AuditLog[]; total: number }> {
    const response = await api.get('/admin/audit-logs', { params });
    return {
      data: (response.data.data as Record<string, unknown>[]).map(a => ({
        id: a.id as number,
        action: a.action as string,
        details: (a.details || {}) as Record<string, unknown>,
        createdAt: (a.created_at || a.createdAt) as string,
        adminUsername: (a.admin_username || a.adminUsername) as string,
        adminEmail: (a.admin_email || a.adminEmail) as string,
      })),
      total: response.data.total as number,
    };
  },
};

export default adminApi;
